import { useState } from 'react';

import type { PingResult } from '@/account/api-keys';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { Spinner } from '@/components/Spinner/Spinner';

import styles from './SecretReveal.module.css';

export interface SecretRevealProps {
  secret: string;
  pingUrl: string;
  /** Null until a test request is sent; `'pending'` while it is in flight. */
  ping?: PingResult | 'pending' | null;
  onPing: () => void;
}

const REASONS: Record<number, string> = {
  200: 'OK',
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  429: 'Too Many Requests',
  500: 'Server Error',
  503: 'Unavailable',
};

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Button type="button" variant="ghost" onClick={copy} aria-label={label}>
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

function Response({ ping }: { ping: SecretRevealProps['ping'] }) {
  if (ping === null || ping === undefined) {
    return (
      <div className={`${styles.response} ${styles.idle}`}>The response will appear here.</div>
    );
  }
  if (ping === 'pending') {
    return (
      <div className={`${styles.response} ${styles.pending}`} aria-busy="true">
        <Spinner />
        <span>Sending GET /v1/ping…</span>
      </div>
    );
  }
  const status =
    ping.status === null ? 'No response' : `${ping.status} ${REASONS[ping.status] ?? ''}`;
  return (
    <div className={styles.response} role="status">
      <div className={styles.responseHeader}>
        <span className={`${styles.pill} ${ping.ok ? styles.pillOk : styles.pillFailed}`}>
          <span aria-hidden="true">{ping.ok ? '✓' : '✕'}</span> {status.trim()}
        </span>
        <span className={styles.latency}>{ping.latencyMs} ms</span>
        <span className={styles.summary}>{ping.summary}</span>
      </div>
      {ping.body ? <pre className={styles.body}>{ping.body}</pre> : null}
    </div>
  );
}

/** A secret shown once, with a way to copy it and to prove it works. */
export function SecretReveal({ secret, pingUrl, ping = null, onPing }: SecretRevealProps) {
  const curl = `curl ${pingUrl} \\\n  -H "Authorization: Bearer ${secret}"`;
  const sent = ping !== null && ping !== 'pending';

  return (
    <div className={styles.reveal}>
      <Callout title="Copy your key now">
        This is the only time it will be shown. Store it somewhere safe, such as a secrets manager.
        If you lose it, regenerate the key.
      </Callout>

      <div className={styles.field}>
        <span className={styles.label} id="secret-label">
          Secret key
        </span>
        <div className={styles.row}>
          <code className={styles.secret} aria-labelledby="secret-label" tabIndex={0}>
            {secret}
          </code>
          <CopyButton text={secret} label="Copy secret key" />
        </div>
      </div>

      <section className={styles.tester} aria-labelledby="tester-heading">
        <div className={styles.testerHeader}>
          <div>
            <h3 id="tester-heading" className={styles.testerTitle}>
              Test your key
            </h3>
            <p className={styles.testerLede}>
              Sends the same request your code would, and shows what the API answers.
            </p>
          </div>
          <Button
            type="button"
            onClick={onPing}
            loading={ping === 'pending'}
            icon={sent ? '↻' : '▶'}
          >
            {sent ? 'Send again' : 'Send test request'}
          </Button>
        </div>

        <div className={styles.console}>
          <div className={styles.consoleBar}>
            <span className={styles.method}>GET</span>
            <span className={styles.url}>{pingUrl}</span>
            <CopyButton text={curl.replace('\\\n  ', '')} label="Copy as curl" />
          </div>
          <pre className={styles.request}>{curl}</pre>
          <Response ping={ping} />
        </div>
      </section>
    </div>
  );
}
